//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import angular from 'angular';
import moment from 'moment-timezone';
import _ from 'underscore';

import * as collectionsApi from '@apis/collections/collectionsApi';
import { CollectionSortType } from '@apis/collections/CollectionSortType';
import { ICollectionSortDto } from '@apis/collections/ICollectionSortDto';
import { CollectionStatusType } from '@apis/collections/CollectionStatusType';
import { CollectionType } from '@apis/collections/CollectionType';
import { ConnectVendorType } from '@apis/collections/ConnectVendorType';
import { ICuralateStore } from '@models/AppState';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { SessionVarsService } from '@services/session-vars-service';
import '@services/user-settings-api.service';
import { UserSettingsApiServiceType } from '@services/user-settings-api.service';
import { actions as shareDataActions } from '@share/actions/shareDataActions';
import * as shareApi from '@share/api/shareApi';
import { IShareCreatorClient } from '@share/models/shareStateModel';
import { IStreamsDrawerState } from '@streams-drawer/model/AppState';
import { IStream } from '@streams-drawer/model/IStream';
import { StreamsDrawerActionNames } from '@streams-drawer/model/StreamsDrawerActionNames';
import {
  CONNECT_CUSTOM_THUMBNAIL_MAX_WIDTH,
  DEFAULT_CONNECT_VENDOR_STATE,
  DEFAULT_MODERATION_STATUS_STATE,
  DEFAULT_SORT_STATE,
  nameToStreamType,
  StreamModalType,
  StreamsDrawerContentViews,
  StreamsDrawerGetItemsContext,
  StreamsDrawerStreamType,
} from '@streams-drawer/streams-drawer-constants';
import { assetFeedsModalActions } from '@streams/actions/assetFeedsModalActions';
import { actions as streamDrawerActions } from '@streams/actions/streamDrawerActions';
import { getIsManualModerationRejectEnabled } from '@redux/selectors/getIsManualModerationRejectEnabled';
import { STREAM_CREATION_ACTIONS } from '@streams/constants/streamCreationActionConstants';
import { DEFAULT_STREAM_NAME } from '@streams/constants/streamCreationConstants';
import { taggingFeedActions } from '@tagging/actions/taggingFeedActions';
import getUrlParams from '@utils/urlUtils';
import { getMedsMaxWidthUrl } from '@utils/meds';
import { BULK_ACTIONS_ACTIONS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';
import { getBulkActionsSelectedItems } from 'app/bulk-actions/selectors/bulk-actions-selector';
import { crl8 } from 'app/curalate.globals';
import { ReduxDispatch } from 'app/curalate.store';
import { GaApi } from 'app/initialization/ga/ga-api';
import { SearchFilterService } from '@legacy-components/filters/search-filter-service';
import { SEARCH_FILTERS as searchFiltersConst } from '@search-filters/constants/searchFilters';
import { setSelectedStreamQueryParam } from '@streams-drawer/utils/setSelectedStreamQueryParam';
import { STREAM_CONTAINER_ID_QUERY_PARAM } from '@streams-drawer/streams-drawer-constants';
import { getIsBazaarvoiceNetworkSyndicationFrontendEnabled } from '@uia/rights/selectors/getUIARights';

export function clearSelectedItems() {
  return {
    type: BULK_ACTIONS_ACTIONS.CLEAR_SELECTED_ITEMS,
  };
}

function closeFiltersSidebar() {
  return {
    type: StreamsDrawerActionNames.CLOSE_FILTERS_SIDEBAR,
  };
}

function closeContentDiscoveryFirstRun() {
  return {
    type: StreamsDrawerActionNames.CLOSE_CONTENT_DISCOVERY_FIRST_RUN,
  };
}

function closeRemovalConfirmationModal() {
  return {
    type: StreamsDrawerActionNames.CLOSE_REMOVAL_CONFIRMATION_MODAL,
  };
}

export function closeStreamsModerationDrawer() {
  return {
    type: StreamsDrawerActionNames.CLOSE_STREAMS_DRAWER,
  };
}

export function closeStreamDeleteConfirmationModal() {
  return {
    type: StreamsDrawerActionNames.CLOSE_DELETE_STREAM_CONFIRMATION_MODAL,
  };
}

function openStreamDeleteConfirmationModal() {
  return {
    type: StreamsDrawerActionNames.OPEN_DELETE_STREAM_CONFIRMATION_MODAL,
  };
}

export function closeStreamModal() {
  return {
    type: StreamsDrawerActionNames.CLOSE_STREAM_MODAL,
  };
}

function discardReceivedContentItems() {
  return {
    type: StreamsDrawerActionNames.DISCARD_RECEIVED_CONTENT_ITEMS,
  };
}

function openFiltersSidebar() {
  return {
    type: StreamsDrawerActionNames.OPEN_FILTERS_SIDEBAR,
  };
}

function openRemovalConfirmationModal() {
  return {
    type: StreamsDrawerActionNames.OPEN_REMOVAL_CONFIRMATION_MODAL,
  };
}

function openStreamModal(modalType: StreamModalType) {
  return {
    type: StreamsDrawerActionNames.OPEN_STREAM_MODAL,
    modalType,
  };
}

function setSelectedStreamType(selectedStreamType: StreamsDrawerStreamType) {
  return {
    type: StreamsDrawerActionNames.SET_SELECTED_STREAM_TYPE,
    selectedStreamType,
  };
}

function requiresLegacyRejectionReasons(
  selectedStreamType: StreamsDrawerStreamType,
  state: ICuralateStore,
) {
  return (
    selectedStreamType === StreamsDrawerStreamType.CONNECT ||
    (selectedStreamType === StreamsDrawerStreamType.TAGGED_BY_CURALTE &&
      !getIsManualModerationRejectEnabled(state))
  );
}

function receiveFilteredContentItemsSucceeded(
  items,
  totalItemCount,
  nextOffset,
) {
  return {
    type: StreamsDrawerActionNames.RECEIVE_FILTERED_CONTENT_ITEMS_SUCCEEDED,
    items,
    nextOffset,
    totalItemCount,
  };
}

function receiveContentDiscoveryFirstRunStatusSucceeded(showFirstRun: boolean) {
  return {
    type: StreamsDrawerActionNames.RECEIVE_FIRST_RUN_STATUS_SUCCEEDED,
    showFirstRun,
  };
}

function receiveContentItemsSucceeded(items, totalItemCount, nextOffset) {
  return {
    type: StreamsDrawerActionNames.RECEIVE_CONTENT_ITEMS_SUCCEEDED,
    items,
    nextOffset,
    totalItemCount,
  };
}

function receiveNextContentItemsSucceeded(items, nextOffset) {
  return {
    type: StreamsDrawerActionNames.RECEIVE_NEXT_CONTENT_ITEMS_SUCCEEDED,
    items,
    nextOffset,
  };
}

function receiveSummaryItemsSucceeded(itemSets) {
  return {
    type: StreamsDrawerActionNames.RECEIVE_SUMMARY_ITEMS_SUCCEEDED,
    itemSets,
  };
}

function finishRefreshingStreams() {
  return {
    type: StreamsDrawerActionNames.FINISH_REFRESHING_STREAMS,
  };
}

function removeMediaFromStream(cuids) {
  return {
    type: StreamsDrawerActionNames.REMOVE_ITEMS,
    cuids,
  };
}

function requestFirstRunStatus() {
  return {
    type: StreamsDrawerActionNames.REQUEST_FIRST_RUN_STATUS,
  };
}

function requestContentItems() {
  return {
    type: StreamsDrawerActionNames.REQUEST_CONTENT_ITEMS,
  };
}

function requestFilteredContentItems() {
  return {
    type: StreamsDrawerActionNames.REQUEST_FILTERED_CONTENT_ITEMS,
  };
}

function requestNextContentItems() {
  return {
    type: StreamsDrawerActionNames.REQUEST_NEXT_CONTENT_ITEMS,
  };
}

function requestSummaryItems() {
  return {
    type: StreamsDrawerActionNames.REQUEST_SUMMARY_ITEMS,
  };
}

function startRefreshingStreams() {
  return {
    type: StreamsDrawerActionNames.START_REFRESHING_STREAMS,
  };
}

export function setContentDiscoveryFlag(isContentDiscoveryEnabled: boolean) {
  return {
    type: StreamsDrawerActionNames.SET_CONTENT_DISCOVERY_FLAG,
    isContentDiscoveryEnabled,
  };
}

function setCurrentContentView(contentView: StreamsDrawerContentViews) {
  return {
    contentView,
    type: StreamsDrawerActionNames.SET_CONTENT_VIEW,
  };
}

function setCurrentFilters(filters) {
  return {
    filters,
    type: StreamsDrawerActionNames.SET_FILTERS,
  };
}

function setLastSavedFilters(filters) {
  return {
    filters,
    type: StreamsDrawerActionNames.SET_LAST_SAVED_FILTERS,
  };
}

function setEndOfContentReached(isEndOfContentReached: boolean) {
  return {
    type: StreamsDrawerActionNames.SET_END_OF_CONTENT_REACHED,
    isEndOfContentReached,
  };
}

function setExistingStream(existingStream: IStream) {
  return {
    type: STREAM_CREATION_ACTIONS.SET_EXISTING_STREAM,
    existingStream,
  };
}

function setRequestContentNonce(nonce: number) {
  return {
    type: StreamsDrawerActionNames.SET_REQUEST_CONTENT_NONCE,
    nonce,
  };
}

export function setSelectedStream(stream: IStream) {
  return {
    stream,
    type: StreamsDrawerActionNames.SELECT_STREAM,
  };
}

export function setCreatorClients(clients: IShareCreatorClient[]) {
  return {
    clients,
    type: StreamsDrawerActionNames.SET_CREATOR_CLIENTS,
  };
}

export function setStreams(streams: IStream[]) {
  return {
    streams,
    type: StreamsDrawerActionNames.SET_STREAMS,
  };
}

export function setCurrentStreamName(name: string) {
  return {
    name,
    type: StreamsDrawerActionNames.SET_CURRENT_STREAM_NAME,
  };
}

export function setRecommendationsStreams(recommendationsStreams: IStream[]) {
  return {
    recommendationsStreams,
    type: StreamsDrawerActionNames.SET_RECOMMENDATIONS_STREAMS,
  };
}

function setSort(sort: ICollectionSortDto['key']) {
  return {
    sort,
    type: StreamsDrawerActionNames.SET_SORT,
  };
}

function setModerationStatus(status: CollectionStatusType) {
  return {
    status,
    type: StreamsDrawerActionNames.SET_MODERATION_STATUS,
  };
}

function setConnectVendor(vendor: string) {
  return {
    vendor,
    type: StreamsDrawerActionNames.SET_CONNECT_VENDOR,
  };
}

function setMentionsMyBrand(mentions: string) {
  return {
    mentions,
    type: StreamsDrawerActionNames.SET_MENTIONS_MY_BRAND,
  };
}

function setAssetFeedsOnboarding(isAssetFeedsOnboarding: boolean) {
  return {
    isAssetFeedsOnboarding,
    type: StreamsDrawerActionNames.SET_ASSET_FEEDS_ONBOARDING,
  };
}

export function resetAssetFeedsDrawer() {
  return {
    type: StreamsDrawerActionNames.RESET_ASSET_FEEDS_DRAWER,
  };
}

export function closeAssetFeedsDrawer() {
  return (dispatch: ReduxDispatch) => {
    dispatch(closeStreamModal());
    dispatch(closeStreamsModerationDrawer());
    dispatch(resetAssetFeedsDrawer());
  };
}

export type TStreamsDrawerActions = ReturnType<typeof StreamsDrawerActions>;

function StreamsDrawerActions(
  bulkActionsApiService,
  collectionFiltersConverter,
  collectionsApiService,
  contentLibraryApiService,
  contentLibraryHelper,
  trackingService: GaApi,
  searchFilterService: SearchFilterService,
  SEARCH_FILTERS: typeof searchFiltersConst,
  sessionVarsService: SessionVarsService,
  UserSettingsApiService: UserSettingsApiServiceType,
) {
  function addSelectedMediaToShare() {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const cuids = getBulkActionsSelectedItems(getState()).map(
        (item) => item.cuid,
      );

      await dispatch(shareDataActions.shareMedia(cuids));

      await dispatch(clearSelectedItems());
      await dispatch(closeStreamsModerationDrawer());

      await dispatch(shareDataActions.resetSharedMedia());
      return dispatch(shareDataActions.getSharedMediaAndStats());
    };
  }

  function refreshStreams() {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(startRefreshingStreams());

      const isContentDiscoveryEnabled =
        getState().streams.streamsDrawer.isContentDiscoveryEnabled;

      await dispatch(streamDrawerActions.fetchRecommendationsStreams());
      const recommendationsStreams =
        getState().streams.streamsDrawer.recommendationsStreams;
      const recommendationStreamCount = recommendationsStreams?.length ?? 0;
      const hasConnectStream = recommendationsStreams
        ?.map((stream) => nameToStreamType(stream))
        ?.includes(StreamsDrawerStreamType.CONNECT);

      const hasRecommendedStreams =
        isContentDiscoveryEnabled && recommendationStreamCount > 0;

      const streams = await collectionsApiService.getAllCollections('Stream');
      dispatch(setStreams(streams));

      const isStreamSelected = getState().streams.streamsDrawer.selectedStream;

      // If no stream is selected, either show a summary view of recommended content
      // or fallback to default stream
      if (!isStreamSelected) {
        let streamToOpen = null;

        // If a stream to open was specified via a query param, attempt to select it.
        const params = getUrlParams();
        const openStreamId = params?.[STREAM_CONTAINER_ID_QUERY_PARAM];
        if (openStreamId) {
          streamToOpen =
            streams.find((stream) => stream.id === openStreamId) ||
            recommendationsStreams.find((stream) => stream.id === openStreamId);
        }

        if (streamToOpen) {
          dispatch(selectStream(streamToOpen));
        } else if (hasRecommendedStreams) {
          // Fetch the connect creator clients for the connect stream
          if (hasConnectStream) {
            const clientId = ClientId.fromString(getState().session.client.id);

            const connectCreatorClientsResponse =
              await shareApi.getCreatorsForConnectPublisherFromStream(clientId);

            dispatch(
              setCreatorClients(connectCreatorClientsResponse.creatorClients),
            );
          }

          // If there is only one recommended stream, select that, otherwise
          // select the summary view
          if (recommendationStreamCount === 1) {
            const recommendedStreamToOpen =
              getState().streams?.streamsDrawer?.recommendationsStreams?.[0];
            const recommendedStreamType = nameToStreamType(
              recommendedStreamToOpen,
            );
            dispatch(setSelectedStreamType(recommendedStreamType));
            dispatch(selectStream(recommendedStreamToOpen));
          } else {
            dispatch(getSummaryItems());

            trackingService.recordEvent(
              trackingService.CATEGORY_STREAMS,
              trackingService.ACTION_STREAMS_SUMMARY_LOADED,
            );

            dispatch(setSelectedStreamType(StreamsDrawerStreamType.SUMMARY));
            setSelectedStreamQueryParam('allrecommendations');
          }
        } else {
          const isShareV2Vendor = getState().session.client.isShareV2Vendor;
          const isBazaarvoiceSyndicationEnabled =
            getIsBazaarvoiceNetworkSyndicationFrontendEnabled(getState());

          const isLegacyShareV2Vendor =
            isShareV2Vendor && !isBazaarvoiceSyndicationEnabled;

          if (isLegacyShareV2Vendor) {
            // If no stream was specified via a query param or we couldn't find it, select the default stream.
            const defaultStreams = streams.filter(
              (stream) => stream.name === DEFAULT_STREAM_NAME,
            );
            if (defaultStreams.length > 0) {
              streamToOpen = defaultStreams[0];
              dispatch(selectStream(streamToOpen));

              if (defaultStreams.length > 1) {
                const clientId = getState().session.client.id;
                throw new Error(
                  `More than one stream found for clientId: ${clientId}`,
                );
              }
            }
          } else if (!isLegacyShareV2Vendor && streams.length > 0) {
            const alphaSortedStreams = streams.sort((l, r) =>
              l.name.toLowerCase().localeCompare(r.name.toLowerCase()),
            );
            dispatch(selectStream(alphaSortedStreams[0]));
          }
        }
      } else if (hasRecommendedStreams) {
        //Fetch rejection reason if user navigating to streams through the moderation popup
        const selectedStreamType =
          getState().streams.streamsDrawer.selectedStreamType;
        if (
          requiresLegacyRejectionReasons(selectedStreamType, getState()) &&
          !getState().streams.streamsDrawer.clientRejectionReasons
        ) {
          dispatch(streamDrawerActions.fetchClientRejectionReasons());
        }
        setSelectedStreamQueryParam(isStreamSelected?.id);
      } else {
        setSelectedStreamQueryParam(isStreamSelected?.id);
      }

      dispatch(finishRefreshingStreams());
    };
  }

  function fetchAssetFeedsStream() {
    return async (dispatch: ReduxDispatch) => {
      dispatch(startRefreshingStreams());
      dispatch(resetAssetFeedsDrawer());

      const assetFeedsStreams = (await collectionsApi.getStreams(
        CollectionType.GOOGLE_FEED_STREAM,
      )) as IStream[];

      if (assetFeedsStreams && assetFeedsStreams.length > 0) {
        const assetFeedsStream = assetFeedsStreams[0];
        dispatch(setStreams([assetFeedsStream]));
        dispatch(selectStream(assetFeedsStream));

        dispatch(
          assetFeedsModalActions.setAssetFeedsStreamSources(assetFeedsStream),
        );
        dispatch(setAssetFeedsOnboarding(false));
      } else {
        dispatch(setAssetFeedsOnboarding(true));
      }

      dispatch(finishRefreshingStreams());
    };
  }

  function completeAssetFeedsOnboarding() {
    return async (dispatch: ReduxDispatch) => {
      await dispatch(assetFeedsModalActions.updateAssetFeedStreamFilters());

      await dispatch(taggingFeedActions.generateFeed());

      dispatch(closeStreamsDrawer());
    };
  }

  function saveAssetFeedsFilters() {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      await dispatch(assetFeedsModalActions.updateAssetFeedStreamFilters());

      dispatch(taggingFeedActions.generateFeed());

      const updatedFilters = getState().streams.streamsDrawer.filters;
      dispatch(setLastSavedFilters(updatedFilters));
    };
  }

  function selectSummary() {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(setSelectedStream(null));
      dispatch(setSelectedStreamType(StreamsDrawerStreamType.SUMMARY));
      if (getState().streams.streamsDrawer.summaryItemSets?.length == 0) {
        dispatch(getSummaryItems());
      }
      setSelectedStreamQueryParam('allrecommendations');
    };
  }

  function selectStream(stream: IStream, forceReloadFilters?: boolean) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const previousStreamType =
        getState().streams.streamsDrawer.selectedStreamType;

      const selectedStreamType = nameToStreamType(stream);
      dispatch(setSelectedStreamType(selectedStreamType));

      dispatch(setSelectedStream(stream));

      if (
        requiresLegacyRejectionReasons(selectedStreamType, getState()) &&
        !getState().streams.streamsDrawer.clientRejectionReasons
      ) {
        dispatch(streamDrawerActions.fetchClientRejectionReasons());
      }

      //Set openStreamID param in the URL for selected stream
      setSelectedStreamQueryParam(stream?.id);

      dispatch(
        setStreamFilters(stream, previousStreamType, forceReloadFilters),
      );

      const streamsDrawerState = getState().streams.streamsDrawer;
      dispatch(fetchContentItems(stream, streamsDrawerState.filters));

      dispatch(clearSelectedItems());
    };
  }

  function setStreamFilters(
    nextStream: IStream,
    previousStreamType: StreamsDrawerStreamType,
    forceReloadFilters?: boolean,
  ) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const streamFilters =
        collectionFiltersConverter.collectionToFilters(nextStream);
      // required to retain the search query between the streams
      const prevSearchText =
        getState().streams.streamsDrawer?.filters?.searchText;

      let newFilters = streamFilters.filtersFilters;
      newFilters = {
        ...newFilters,
        searchText: prevSearchText,
      };
      const newSources = {
        ...streamFilters.sourcesFilters,
        contentGroup: 'Nothing',
      };

      const sourcesSearchFilter = SEARCH_FILTERS.STREAM_SOURCES;

      const filterSetRegistered =
        searchFilterService.getRegisteredSet(sourcesSearchFilter);

      if (filterSetRegistered) {
        searchFilterService.updateSet(sourcesSearchFilter, newSources);
      } else {
        searchFilterService.registerNewFilterSet(
          sourcesSearchFilter,
          newSources,
        );
      }

      const nextStreamType =
        getState().streams.streamsDrawer.selectedStreamType;

      const previousStreamWasStandard =
        previousStreamType === StreamsDrawerStreamType.STANDARD;
      const nextStreamIsStandard =
        nextStreamType === StreamsDrawerStreamType.STANDARD;
      const shouldApplyAllNewFilters =
        !(previousStreamWasStandard && nextStreamIsStandard) ||
        forceReloadFilters;

      const streamConfigSpamValue = nextStream?.filters?.spam;
      const streamExcludedSources = nextStream?.filters?.excludedContentSources;
      const shouldApplyNewSpamFilter =
        previousStreamWasStandard && nextStreamIsStandard;

      if (shouldApplyAllNewFilters) {
        // New filters are re-applied in 3 situations:
        // 1. We're switching to a recommended stream
        // 2. We're switching to a standard stream for the first time,
        // 3. The forceReload param was true. This is currently used when creating a straem or updating stream sources
        if (nextStreamType === StreamsDrawerStreamType.STANDARD) {
          newFilters = {
            ...newFilters,

            // If the stream config has spam filtering disabled, respect that value.
            // Otherwise, enable spam filtering
            spamFiltering: streamConfigSpamValue === false ? [] : ['true'],
          };
        }

        // If stream has persisted sort, use it. Otherwise, use the default sort
        let sort: ICollectionSortDto['key'] = DEFAULT_SORT_STATE;
        if (nextStream.sort) {
          sort = nextStream.sort.key;
        }
        // If stream is of type "Tagged by Curalate" or "connect", set a default moderation
        // status param value or use the one included in the stream response.
        // Otherwise, leave the value undefined as it is not needed for standard
        // streams.
        let moderationStatus;
        if (
          nextStreamType === StreamsDrawerStreamType.TAGGED_BY_CURALTE ||
          nextStreamType === StreamsDrawerStreamType.CONNECT
        ) {
          moderationStatus = DEFAULT_MODERATION_STATUS_STATE;
          if (nextStream.moderationStatus) {
            moderationStatus = nextStream.moderationStatus;
          }
        }
        // If stream is of type "Connect", set a default connect vendor
        // param value or use the one included in the stream response.
        // Otherwise, leave the value undefined as it is not needed for standard
        // streams.
        let connectVendor;
        let mentionsMyBrand;
        if (nextStreamType === StreamsDrawerStreamType.CONNECT) {
          connectVendor = DEFAULT_CONNECT_VENDOR_STATE;
          if (nextStream.connectVendor) {
            connectVendor = nextStream.connectVendor;
          }
          dispatch(setStreamMentions(mentionsMyBrand));
        }
        dispatch(setLastSavedFilters(newFilters));
        dispatch(setCurrentFilters(newFilters));
        dispatch(setStreamSort(sort));
        dispatch(setStreamModerationStatus(moderationStatus));
        dispatch(setStreamConnectVendor(connectVendor));
      } else if (shouldApplyNewSpamFilter) {
        // We want to keep user selected filters when switching between standard streams,
        // with the exception being the Spam filter.
        const existingFilters = getState().streams.streamsDrawer.filters;
        const updatedFilters = {
          ...existingFilters,
          excludedContentSources: streamExcludedSources,
          spamFiltering: streamConfigSpamValue === false ? [] : ['true'],
        };

        dispatch(setLastSavedFilters(updatedFilters));
        dispatch(setCurrentFilters(updatedFilters));
      }
    };
  }

  function setStreamSort(sort: ICollectionSortDto['key']) {
    return (dispatch: ReduxDispatch) => {
      dispatch(setSort(sort));
      searchFilterService.updateSet(SEARCH_FILTERS.STREAMS_FILTERS_SORT, {
        orderBy: [sort],
      });
    };
  }

  function setStreamModerationStatus(status: CollectionStatusType) {
    return (dispatch: ReduxDispatch) => {
      dispatch(setModerationStatus(status));
      searchFilterService.updateSet(
        SEARCH_FILTERS.STREAM_FILTERS_MODERATION_STATUS,
        { moderationStatus: [status] },
      );
    };
  }

  function setStreamMentions(mentions: string) {
    return (dispatch: ReduxDispatch) => {
      dispatch(setMentionsMyBrand(mentions));
      searchFilterService.updateSet(
        SEARCH_FILTERS.STREAM_FILTERS_MENTIONS_STATUS,
        {
          mentionsMyBrand: mentions ? [mentions] : [],
        },
      );
    };
  }

  function setStreamConnectVendor(vendor: string) {
    return (dispatch: ReduxDispatch) => {
      dispatch(setConnectVendor(vendor));
      searchFilterService.updateSet(
        SEARCH_FILTERS.STREAM_FILTERS_CONNECT_VENDOR,
        { connectVendor: [vendor] },
      );
    };
  }

  function getSummaryItems() {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(requestSummaryItems());

      const recommendationsStreams =
        getState().streams.streamsDrawer.recommendationsStreams;

      const defaultApiParams = {
        limit: 10,
        offset: 0,
        contentGroup: 'Nothing', // Ensures we get all media instead of team media. See DEV-39013.
      };

      const promises = recommendationsStreams.map((stream) => {
        const collectionFilters =
          collectionFiltersConverter.collectionToFilters(stream);

        const apiParams = {
          collectionId: stream.id,
          ...defaultApiParams,
          ...collectionFilters.filtersFilters,
          ...collectionFilters.sourcesFilters,
          orderBy: stream.sort.key,
        };

        return contentLibraryApiService.getContentItemsAsync(apiParams);
      });

      Promise.all(promises).then((response) => {
        const itemSets = response.map((items: any, index) => {
          const stream = recommendationsStreams[index];
          const streamType = nameToStreamType(stream);

          const summaryItems = items.contentItems.map((item) => {
            const clientId = getState().session.client.id;
            const cuid = `${item.itemType}.${item.id}.${clientId}`;

            return {
              ...item,
              onAddToGalleryCallback: () => {
                trackingService.recordEvent(
                  trackingService.CATEGORY_STREAMS,
                  trackingService.ACTION_STREAMS_SHARE_IMAGE_TO_GALLERY_FROM_SUMMARY,
                  `cuid: ${cuid}; collection-type=${streamType}`,
                );
              },
              onImageClickedCallback: () => {
                trackingService.recordEvent(
                  trackingService.CATEGORY_STREAMS,
                  trackingService.ACTION_STREAMS_IMAGE_CLICKED_FROM_SUMMARY,
                  streamType,
                );
              },
            };
          });

          trackingService.recordEvent(
            trackingService.CATEGORY_STREAMS,
            trackingService.ACTION_STREAMS_CAROUSEL_VIEWED,
            streamType,
          );

          return {
            stream,
            items: summaryItems,
          };
        });
        dispatch(receiveSummaryItemsSucceeded(itemSets));
      });
    };
  }

  function getContentItems(
    context: StreamsDrawerGetItemsContext,
    stream: IStream,
    streamFilters: any,
    offset: number = 0,
    limit: number = 30,
  ) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const state: IStreamsDrawerState = getState().streams.streamsDrawer;
      const {
        isFetchingNextItems,
        isEndOfContentReached,
        selectedStreamType,
        sort,
        moderationStatus,
        mentionsMyBrand,
        connectVendor,
      } = state;

      if (isFetchingNextItems || isEndOfContentReached) {
        return;
      }

      switch (context) {
        case StreamsDrawerGetItemsContext.FILTERS_CHANGE:
          dispatch(requestFilteredContentItems());
          break;
        case StreamsDrawerGetItemsContext.FIRST_PAGE:
          dispatch(requestContentItems());
          break;
        case StreamsDrawerGetItemsContext.NEXT_PAGE:
          dispatch(requestNextContentItems());
      }

      const collectionFilters =
        collectionFiltersConverter.collectionToFilters(stream);

      const apiParams = {
        ...collectionFilters.filtersFilters,
        ...collectionFilters.sourcesFilters,
        ...streamFilters,
      };

      apiParams.collectionId = stream.id;
      apiParams.contentGroup = 'Nothing';
      apiParams.limit = limit;
      apiParams.offset = offset;
      apiParams.orderBy = sort;
      apiParams.moderationStatus = moderationStatus;

      if (mentionsMyBrand) {
        const mentionValue = [mentionsMyBrand].flat()[0];

        if (mentionValue) {
          const instagramMentions = stream.sourcesMetadata?.instagramMentions;
          if (
            instagramMentions &&
            Object.values(instagramMentions).some(
              (metadata) => metadata?.username === mentionValue,
            )
          ) {
            apiParams.instagramMentionedUsernames = [mentionValue];
          } else {
            const tiktokMentions = stream.sourcesMetadata?.tiktokMentions;
            if (
              tiktokMentions &&
              Object.values(tiktokMentions).some(
                (metadata) => metadata?.username === mentionValue,
              )
            ) {
              apiParams.tiktokMentionedUsernames = [mentionValue];
            } else {
              apiParams.mentionsMyBrand = [mentionValue];
            }
          }
        } else {
          apiParams.mentionsMyBrand = [];
        }
      } else {
        apiParams.mentionsMyBrand = [];
      }

      apiParams.connectVendor =
        connectVendor && connectVendor !== ConnectVendorType.All
          ? [connectVendor]
          : null;
      apiParams.hydrateWithDeetsItems = true;

      // Purposefully separate these out rather than integrate within collectionToFilters
      //  filters carry over between streams and we can't afford to let the minute offset filter
      //  carry over to other streams.
      if (stream.filters?.minuteOffsetRange) {
        apiParams.minuteOffsetRange = stream.filters.minuteOffsetRange;
      }

      const cachedNonce = Date.now();
      dispatch(setRequestContentNonce(cachedNonce));

      return contentLibraryApiService
        .getContentItemsAsync(apiParams)
        .then(async (response) => {
          // if selected stream has switched mid-flight, ignore the returned items from this fetch
          const currentNonce =
            getState().streams.streamsDrawer.requestContentNonce;
          if (cachedNonce !== currentNonce) {
            dispatch(discardReceivedContentItems());
            return;
          }

          let items;
          let nextOffset;
          if (offset > 0) {
            items = contentLibraryHelper.contentItemConcat(
              state.items,
              response.contentItems,
            );
            nextOffset = offset + limit;
            if (items.length === state.items.length) {
              dispatch(setEndOfContentReached(true));
            }
          } else {
            items = response.contentItems;
            nextOffset = response.contentItems.length;
          }

          if (items.length >= response.totalCount) {
            dispatch(setEndOfContentReached(true));
          }

          const totalItemCount = response.totalCount;

          const clientId = ClientId.fromString(getState().session.client.id);

          let itemDetailsByPartialCuid: ReturnType<
            typeof contentLibraryApiService.getItemDetailsAsync
          > = {};
          const cuidsNeedingDetails = response.contentItems.map((item) =>
            Cuid.build(item, clientId),
          );
          try {
            itemDetailsByPartialCuid =
              await contentLibraryApiService.getItemDetailsAsync(
                cuidsNeedingDetails,
              );
          } catch (e) {
            // If the item details request fails, swallow the error and log it. We use these
            // details for some non-critical metadata overlays.
            crl8.logger?.cloud?.error?.(
              'Failed to load item details for the streams drawer',
              e,
            );
          }

          const isConnectStreamSelected =
            selectedStreamType === StreamsDrawerStreamType.CONNECT;

          // Attach stream source and tracking cb to items
          items = items.map((item) => {
            const partialCuid = PartialCuid.build(item);
            const cuid = Cuid.fromPartial(partialCuid, clientId);

            const customThumbnailFileId =
              item.customThumbnailMetadata?.customThumbnailFileId;
            const imageUrl =
              isConnectStreamSelected && customThumbnailFileId
                ? getMedsMaxWidthUrl(
                    customThumbnailFileId,
                    CONNECT_CUSTOM_THUMBNAIL_MAX_WIDTH,
                  )
                : item.imageUrl;

            return {
              ...item,
              imageUrl,
              streamType: selectedStreamType,
              itemDetails:
                item.itemDetails || itemDetailsByPartialCuid[partialCuid],
              onAddToGalleryCallback: () => {
                trackingService.recordEvent(
                  trackingService.CATEGORY_STREAMS,
                  trackingService.ACTION_STREAMS_SHARE_IMAGE_TO_GALLERY_FROM_STREAM,
                  `cuid: ${cuid}; collection-type=${selectedStreamType}`,
                );
              },
              onImageClickedCallback: () => {
                trackingService.recordEvent(
                  trackingService.CATEGORY_STREAMS,
                  trackingService.ACTION_STREAMS_IMAGE_CLICKED_FROM_STREAM,
                  selectedStreamType,
                );
              },
            };
          });

          switch (context) {
            case StreamsDrawerGetItemsContext.FILTERS_CHANGE:
              return dispatch(
                receiveFilteredContentItemsSucceeded(
                  items,
                  totalItemCount,
                  nextOffset,
                ),
              );
            case StreamsDrawerGetItemsContext.FIRST_PAGE:
              return dispatch(
                receiveContentItemsSucceeded(items, totalItemCount, nextOffset),
              );
            case StreamsDrawerGetItemsContext.NEXT_PAGE:
              return dispatch(
                receiveNextContentItemsSucceeded(items, nextOffset),
              );
          }
        });
    };
  }

  function fetchContentItems(stream: IStream, streamFilters) {
    return (dispatch: ReduxDispatch) => {
      dispatch(setEndOfContentReached(false));
      dispatch(
        getContentItems(
          StreamsDrawerGetItemsContext.FIRST_PAGE,
          stream,
          streamFilters,
        ),
      );
    };
  }

  function fetchNextContentItems(stream: IStream, streamFilters: any) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const state: IStreamsDrawerState = getState().streams.streamsDrawer;
      if (state.isFetchingItems || state.isFetchingNextItems) {
        return;
      }
      dispatch(
        getContentItems(
          StreamsDrawerGetItemsContext.NEXT_PAGE,
          stream,
          streamFilters,
          state.nextOffset,
        ),
      );
    };
  }

  function getContentDiscoveryFirstRunStatus() {
    return async (dispatch: ReduxDispatch) => {
      dispatch(requestFirstRunStatus());

      let showFirstRun;
      const contentDiscoveryFirstRunStatus =
        await UserSettingsApiService.getContentDiscoveryFirstRunStatus();

      if (contentDiscoveryFirstRunStatus) {
        if (contentDiscoveryFirstRunStatus.isClosed) {
          showFirstRun = false;
        } else {
          const firstViewTimestamp =
            contentDiscoveryFirstRunStatus.firstViewTimestamp;
          const sevenDaysLater = moment.unix(firstViewTimestamp).add(7, 'days');

          if (moment().isAfter(sevenDaysLater)) {
            showFirstRun = false;
          } else {
            showFirstRun = true;
          }
        }
      } else {
        const firstViewTimestamp = Date.now();
        UserSettingsApiService.setContentDiscoveryFirstRunStatus({
          firstViewTimestamp,
          isClosed: false,
        });
        showFirstRun = true;
      }

      dispatch(receiveContentDiscoveryFirstRunStatusSucceeded(showFirstRun));
    };
  }

  function closeContentDiscoveryFirstRunCard() {
    return (dispatch: ReduxDispatch) => {
      dispatch(closeContentDiscoveryFirstRun());
      UserSettingsApiService.setContentDiscoveryFirstRunStatus({
        isClosed: true,
      });
    };
  }

  function removeSelectedMediaFromStream() {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const selectedStream = getState().streams.streamsDrawer.selectedStream;
      const selectedCuids = getBulkActionsSelectedItems(getState()).map(
        (item) => item.cuid,
      );

      await bulkActionsApiService.removeFromCollectionAsync(
        selectedCuids,
        selectedStream.id,
      );

      dispatch(removeMediaFromStream(selectedCuids));
      dispatch(clearSelectedItems());
      dispatch(closeRemovalConfirmationModal());

      trackingService.recordEvent(
        trackingService.CATEGORY_STREAMS,
        trackingService.ACTION_STREAMS_REMOVE_ITEMS_FROM_STREAM,
        'numberOfItems',
        selectedCuids.length.toString(),
      );
    };
  }

  function updateFilters(filters: any) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const streamsDrawerState = getState().streams.streamsDrawer;
      if (!streamsDrawerState.selectedStream) {
        return;
      }

      const combinedFilters = {
        ...streamsDrawerState.filters,
        ...filters,
      };

      if (_(combinedFilters).isEqual(streamsDrawerState.filters)) {
        return;
      } else {
        dispatch(setEndOfContentReached(false));
        dispatch(
          getContentItems(
            StreamsDrawerGetItemsContext.FIRST_PAGE,
            streamsDrawerState.selectedStream,
            combinedFilters,
          ),
        );
        dispatch(setCurrentFilters(combinedFilters));
      }
    };
  }

  function updateSort(newSort: CollectionSortType) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const streamsDrawerState = getState().streams.streamsDrawer;

      if (
        !streamsDrawerState.selectedStream ||
        streamsDrawerState.sort === newSort
      ) {
        return;
      }

      dispatch(setEndOfContentReached(false));
      dispatch(setSort(newSort));
      dispatch(
        getContentItems(
          StreamsDrawerGetItemsContext.FIRST_PAGE,
          streamsDrawerState.selectedStream,
          streamsDrawerState.filters,
        ),
      );
    };
  }

  function updateModerationStatus(newModerationStatus: CollectionStatusType) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const streamsDrawerState = getState().streams.streamsDrawer;

      if (
        !streamsDrawerState.selectedStream ||
        streamsDrawerState.moderationStatus === newModerationStatus
      ) {
        return;
      }

      dispatch(setEndOfContentReached(false));
      dispatch(setModerationStatus(newModerationStatus));
      dispatch(
        getContentItems(
          StreamsDrawerGetItemsContext.FIRST_PAGE,
          streamsDrawerState.selectedStream,
          streamsDrawerState.filters,
        ),
      );
    };
  }

  // This is called when the connect vendor filter dropdown is changed
  // in the connectstream.
  function updateConnectVendor(newConnectVendor: string) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const streamsDrawerState = getState().streams.streamsDrawer;

      if (
        !streamsDrawerState.selectedStream ||
        streamsDrawerState.connectVendor === newConnectVendor
      ) {
        return;
      }

      dispatch(setEndOfContentReached(false));
      dispatch(setConnectVendor(newConnectVendor));
      dispatch(
        getContentItems(
          StreamsDrawerGetItemsContext.FIRST_PAGE,
          streamsDrawerState.selectedStream,
          streamsDrawerState.filters,
        ),
      );
    };
  }

  // This is called when the Mentions My brand checkbox state is changed
  function updateMentionsMyBrand(newMentions: string) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const streamsDrawerState = getState().streams.streamsDrawer;
      if (
        !streamsDrawerState.selectedStream ||
        streamsDrawerState.mentionsMyBrand === newMentions
      ) {
        return;
      }

      dispatch(setEndOfContentReached(false));
      dispatch(setMentionsMyBrand(newMentions));
      dispatch(
        getContentItems(
          StreamsDrawerGetItemsContext.FIRST_PAGE,
          streamsDrawerState.selectedStream,
          streamsDrawerState.filters,
        ),
      );
    };
  }

  function setContentView(contentView) {
    return setCurrentContentView(contentView);
  }

  function openNewStreamModal() {
    trackingService.recordEvent(
      trackingService.CATEGORY_STREAMS,
      trackingService.ACTION_STREAMS_NEW_STREAM_CTA_CLICKED,
    );

    return (dispatch: ReduxDispatch) => {
      dispatch(openStreamModal(StreamModalType.STANDARD));
    };
  }

  function openEditStreamModal(modalType: StreamModalType) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const selectedStream = getState().streams.streamsDrawer.selectedStream;

      if (selectedStream) {
        dispatch(setExistingStream(selectedStream));
        dispatch(openStreamModal(modalType));
      }
    };
  }

  function confirmAssetFeedsDrawerClose() {
    return (dispatch: ReduxDispatch) => {
      dispatch(openStreamModal(StreamModalType.ASSET_FEEDS_CLOSE_CONFIRMATION));
    };
  }

  function closeStreamsDrawer() {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(closeStreamsModerationDrawer());

      // Deselect stream so that the galleries streams drawer loads its default stream
      const selectedStreamType =
        getState().streams.streamsDrawer.selectedStreamType;
      if (selectedStreamType === StreamsDrawerStreamType.ASSET_FEEDS) {
        dispatch(resetAssetFeedsDrawer());
      }
    };
  }

  return {
    addSelectedMediaToShare,
    closeFiltersSidebar,
    closeRemovalConfirmationModal,
    closeStreamModal,
    confirmAssetFeedsDrawerClose,
    closeStreamsDrawer,
    closeContentDiscoveryFirstRunCard,
    completeAssetFeedsOnboarding,
    getContentDiscoveryFirstRunStatus,
    fetchAssetFeedsStream,
    fetchContentItems,
    fetchNextContentItems,
    getSummaryItems,
    refreshStreams,
    openEditStreamModal,
    openStreamDeleteConfirmationModal,
    closeStreamDeleteConfirmationModal,
    openFiltersSidebar,
    openNewStreamModal,
    openRemovalConfirmationModal,
    removeSelectedMediaFromStream,
    saveAssetFeedsFilters,
    selectStream,
    selectSummary,
    setContentView,
    updateFilters,
    updateSort,
    updateModerationStatus,
    updateConnectVendor,
    updateMentionsMyBrand,
  };
}

angular
  .module('curalate')
  .service('StreamsDrawerActions', [
    'BulkActionsApiService',
    'CollectionFiltersConverter',
    'CollectionsApiService',
    'ContentLibraryApiService',
    'ContentLibraryHelper',
    'TrackingService',
    'SearchFilterService',
    'SEARCH_FILTERS',
    'SessionVarsService',
    'UserSettingsApiService',
    StreamsDrawerActions,
  ]);
